import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { User } from "@/lib/columns";
import { UserCircle, Phone, Mail, MapPin, CreditCard, Calendar, Users, Star } from "lucide-react";
import { formatDateTimeGMT5 } from "@/utils/timezone";

interface UserDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
}

const roleColors: Record<string, string> = {
  user: "bg-blue-100 text-blue-800 border-blue-300",
  expert: "bg-purple-100 text-purple-800 border-purple-300",
  influencer: "bg-pink-100 text-pink-800 border-pink-300",
  official: "bg-yellow-100 text-yellow-800 border-yellow-300",
  admin: "bg-red-100 text-red-800 border-red-300",
  guest: "bg-gray-100 text-gray-700 border-gray-300",
};

function Field({ label, value, icon }: { label: string; value?: string | number | null; icon?: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground flex items-center gap-1">
        {icon} {label}
      </p>
      <p className="font-medium text-sm">{value || <span className="text-muted-foreground italic">—</span>}</p>
    </div>
  );
}

export default function UserDetailDialog({ open, onOpenChange, user }: UserDetailDialogProps) {
  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-xl">
            <div className="flex items-center gap-3">
              {user.image_url ? (
                <img src={user.image_url} alt={user.username || ""} className="w-12 h-12 rounded-full object-cover border-2" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                  <UserCircle className="w-8 h-8 text-muted-foreground" />
                </div>
              )}
              <div>
                <span>{user.username || `User #${user.id}`}</span>
                <div className="mt-1">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${roleColors[user.role] || "bg-gray-100 text-gray-700 border-gray-300"}`}>
                    {user.role}
                  </span>
                </div>
              </div>
            </div>
          </DialogTitle>
          <DialogDescription>Full profile details for user #{user.id}</DialogDescription>
        </DialogHeader>

        <div className="space-y-8 pt-2">
          {/* Contact */}
          <section>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-2">
              <Phone className="w-4 h-4" /> Contact Info
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="Email" value={user.email} icon={<Mail className="w-3 h-3" />} />
              <Field label="Phone Number" value={user.phone_number} icon={<Phone className="w-3 h-3" />} />
              <Field label="Gender" value={user.gender} />
              <Field label="Country" value={user.country} icon={<MapPin className="w-3 h-3" />} />
              <Field label="Country Code" value={user.country_code} />
              <Field label="Member Name" value={user.member_name} />
            </div>
          </section>

          {/* DXN / Distributor Info */}
          <section>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-2">
              <CreditCard className="w-4 h-4" /> DXN / Distributor Info
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="Distributor Code" value={user.distributor_code} />
              <Field label="Distributor Rank" value={user.distributor_rank} />
              <Field label="Sponsor Name" value={user.sponsor_name} />
              <Field label="Sponsor Code" value={user.sponsor_code} />
              <Field label="Sponsor Rank" value={user.sponsor_rank} />
            </div>
          </section>

          {/* Referral */}
          <section>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-2">
              <Users className="w-4 h-4" /> Referral
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="Referral Code" value={user.referral_code} icon={<Star className="w-3 h-3" />} />
              <Field label="Referred By" value={user.referrer ? (user.referrer.username || `#${user.referrer.id}`) : null} />
            </div>
          </section>

          {/* System IDs */}
          <section>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">System IDs</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <Field label="User ID" value={user.id} />
              <Field label="G-ID" value={user.referral_code} />
              {/* <Field label="D ID" value={user.d_id} />
              <Field label="I ID" value={user.i_id} /> */}
            </div>
          </section>

          {/* Timestamps */}
          <section>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4" /> Timestamps
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Joined" value={user.created_at ? formatDateTimeGMT5(new Date(user.created_at)) : null} />
              <Field label="Last Updated" value={user.updated_at ? formatDateTimeGMT5(new Date(user.updated_at)) : null} />
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
